import { useState } from 'react';
import { ProofImage } from '../cgOrders/ProofImage';
import { ArtworkDownload } from '../cgOrders/ArtworkDownload';
import { Badge, InlineError, Input, Select, Skeleton } from '../../components/ui';
import {
  reviewErrorMessage,
  useComponentTypes,
  useReviewOption,
  useSubmissionOptions,
} from './sampleReview';
import { submissionComponents } from '../artwork/componentVocabulary';
import { buildSlots, isFullyApproved, rejectionNotes } from './sampleSlots';
import type { ComponentSlot, SlotState } from './sampleSlots';
import type { OptionVerdict, SubmissionOption } from './sampleReview';

/**
 * Reviewing a sample submission, component by component.
 *
 * The state each slot is in decides what happens next for a real person: a
 * locked slot is settled, a slot needing revision sends the factory back to
 * work, and a slot still awaiting review is work this reviewer owes. Those
 * words are on screen rather than left for somebody to infer from a row of
 * coloured pills.
 *
 * Every verdict writes on click. There is no Save button, matching the rest of
 * the Hub, and the write is optimistic — a reviewer working through a dozen
 * cards should never wait on a round trip between them.
 */

const SLOT_LABELS: Record<SlotState, string> = {
  locked: 'Approved',
  revision_required: 'Revision required',
  awaiting_review: 'Awaiting review',
  // TAP's word for the same state, from the other side of the table: the
  // factory may still add options, so a decision now judges a set that can
  // still grow. Named rather than folded into "Awaiting review" so a reviewer
  // can tell the two apart at a glance.
  collecting: 'Supplier still adding',
  empty: 'Nothing uploaded',
};

const SLOT_TONES: Record<SlotState, 'success' | 'warning' | 'brand' | 'neutral'> = {
  locked: 'success',
  revision_required: 'warning',
  awaiting_review: 'brand',
  collecting: 'neutral',
  empty: 'neutral',
};

export function SampleReviewPanel({
  submissionId,
  canReview,
}: {
  submissionId: string;
  /** False for a supplier looking at their own submission. */
  canReview: boolean;
}) {
  const components = useComponentTypes();
  const options = useSubmissionOptions(submissionId);
  const review = useReviewOption(submissionId);
  const [error, setError] = useState('');

  if (components.isPending || options.isPending) {
    return <Skeleton lines={4} label="Loading sample options" />;
  }
  if (components.isError || options.isError) {
    return <InlineError>Sample options could not be loaded.</InlineError>;
  }

  const slots = buildSlots(
    submissionComponents(
      components.data.map((component) => component.id),
      options.data.map((option) => option.component_type),
    ),
    options.data,
  );
  const finished = isFullyApproved(slots);

  return (
    <section className="sample-review">
      <div className="sample-review-heading">
        <h3 className="task-panel-heading">Components</h3>
        {finished ? (
          <Badge tone="success">All components approved</Badge>
        ) : (
          <span className="sample-review-progress">
            {slots.filter((slot) => slot.state === 'locked').length} of {slots.length} approved
          </span>
        )}
      </div>

      {error === '' ? null : <InlineError>{error}</InlineError>}

      {slots.map((slot) => (
        <SlotCard
          key={slot.component}
          slot={slot}
          canReview={canReview && !review.isPending}
          onVerdict={(optionId, verdict, feedback) => {
            setError('');
            review.mutate(
              feedback === undefined ? { optionId, verdict } : { optionId, verdict, feedback },
              { onError: (cause: Error) => setError(reviewErrorMessage(cause.message)) },
            );
          }}
        />
      ))}
    </section>
  );
}

function SlotCard({
  slot,
  canReview,
  onVerdict,
}: {
  slot: ComponentSlot;
  canReview: boolean;
  onVerdict: (optionId: string, verdict: OptionVerdict, feedback?: string) => void;
}) {
  const notes = rejectionNotes(slot);

  return (
    <div className={`sample-slot sample-slot--${slot.state}`}>
      <div className="sample-slot-header">
        <h4 className="sample-slot-name">{slot.component}</h4>
        <Badge tone={SLOT_TONES[slot.state]}>{SLOT_LABELS[slot.state]}</Badge>
        {slot.round > 1 ? <span className="sample-slot-round">Round {slot.round}</span> : null}
      </div>

      {slot.state === 'empty' ? (
        <p className="task-panel-text">
          The supplier has not uploaded anything for this component.
        </p>
      ) : (
        <ul className="sample-option-grid">
          {slot.options.map((option, index) => (
            <OptionCard
              key={option.id}
              option={option}
              index={index}
              // A locked slot stops accepting verdicts on its OTHER options:
              // the winner is settled, and judging the losers again would only
              // be refused by the one-winner index.
              //
              // And an option the factory has not SENT is not ours to rule on
              // (20261119000200). They are still deciding what to show; a
              // verdict now would lock the component on a photo nobody
              // offered. guard_option_decision refuses it at the table too, so
              // this is about not offering a control that can only fail.
              canReview={
                canReview &&
                option.submitted_at !== null &&
                (slot.winner === null || option.id === slot.winner.id)
              }
              onVerdict={onVerdict}
            />
          ))}
        </ul>
      )}

      {notes.length === 0 ? null : (
        <details className="sample-slot-history">
          <summary>Feedback given so far ({notes.length})</summary>
          <ul className="sample-note-list">
            {notes.map((note) => (
              <li key={`${note.label}-${note.feedback}`}>
                <strong>{note.label}:</strong> {note.feedback}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function OptionCard({
  option,
  index,
  canReview,
  onVerdict,
}: {
  option: SubmissionOption;
  index: number;
  canReview: boolean;
  onVerdict: (optionId: string, verdict: OptionVerdict, feedback?: string) => void;
}) {
  // Local while typing, committed on blur. Writing per keystroke would be a
  // request per character and would fight the optimistic cache.
  const [feedback, setFeedback] = useState(option.feedback);

  const label = option.label.trim() === '' ? `Option ${String(index + 1)}` : option.label;
  // Visible to TAP, deliberately, but marked as what it is: the factory can
  // see we can see it, and neither side is guessing about what has been
  // offered. Hiding drafts would have TAP asking for samples that are sitting
  // on the screen.
  const draft = option.submitted_at === null;

  return (
    <li className={`sample-option sample-option--${option.verdict}`}>
      {draft ? <Badge tone="neutral">Not sent yet</Badge> : null}
      {/* The anchor still opens the file; the THUMBNAIL inside it must come
          from Drive's thumbnail endpoint — the share URL is a viewer page and
          renders a broken icon in an <img>. Same bug ProofImage exists for. */}
      <a className="sample-option-image" href={option.url} target="_blank" rel="noreferrer">
        <ProofImage url={option.url} alt={label} />
      </a>
      <ArtworkDownload url={option.url} component={label} />

      <div className="sample-option-body">
        <span className="sample-option-label">{label}</span>

        {canReview ? (
          <Select
            label={`Verdict for ${label}`}
            labelHidden
            value={option.verdict}
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approve' },
              { value: 'denied', label: 'Deny' },
            ]}
            onChange={(event) => onVerdict(option.id, event.target.value as OptionVerdict)}
          />
        ) : (
          <Badge tone={option.verdict === 'approved' ? 'success' : 'neutral'}>
            {option.verdict === 'approved'
              ? 'Winning option'
              : option.verdict === 'denied'
                ? 'Denied'
                : 'Pending'}
          </Badge>
        )}

        {canReview ? (
          <Input
            label={`Feedback for ${label}`}
            labelHidden
            placeholder="Plating too shiny…"
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
            onBlur={() => {
              if (feedback !== option.feedback) {
                onVerdict(option.id, option.verdict, feedback);
              }
            }}
          />
        ) : option.feedback.trim() === '' ? null : (
          <p className="sample-option-feedback">{option.feedback}</p>
        )}
      </div>
    </li>
  );
}
